import { PRIMARY_PRODUCT } from '../constants';
import { type IntentContext, type IntentMatcher } from '../types';
import { emptyMatcher } from './matchers/empty';
import { jqlMatcher } from './matchers/jql';
import { numericMatcher } from './matchers/numeric';
import { singleWordMatcher } from './matchers/single-word';
/**
 * Matchers that apply regardless of context.
 *
 * Intentionally empty. Everything ported so far is Jira intent detection, so
 * there is nothing yet that should run for every product.
 *
 * The slot is kept because cross-product matchers are expected later, and
 * because it documents that "universal" is a deliberate category rather than
 * somewhere matchers drift into by default. Adding one here switches it on for
 * every context at once, so it should be a considered decision.
 */
const UNIVERSAL_MATCHERS: readonly IntentMatcher[] = Object.freeze([]);

/**
 * Jira matchers, in the order upstream evaluates them.
 *
 * PORTED FROM: `GninHeuristicStrategy.classifyInternal`
 * https://bitbucket.org/atlassian/xp-knowledge-discovery/src/f02f6dd19b50e91f20c6ab94d493ce7764e76eb1/src/main/java/io/atlassian/micros/xpknowledgediscovery/services/strategy/GninHeuristicStrategy.java#lines-65
 *
 * This order is **not** a local preference — it is the upstream sequence of
 * `if` statements, and changing it changes classifications. In particular
 * `empty` must precede `jql`, because `isJql` reports blank input as JQL.
 *
 * Universal matchers are appended last, so a future cross-product matcher can
 * never pre-empt a confident Jira-specific one.
 */
const JIRA_MATCHERS: readonly IntentMatcher[] = Object.freeze([
	emptyMatcher,
	jqlMatcher,
	numericMatcher,
	singleWordMatcher,
	...UNIVERSAL_MATCHERS,
]);

/**
 * Decides which matchers run, and in what order, for a given context.
 *
 * This is the **single place** where inclusion, exclusion and ordering are
 * decided. Keeping it in one pure function means:
 *
 * - the full behaviour for any context can be read at a glance, instead of
 *   being reconstructed from predicates scattered across matcher files;
 * - the configuration itself is directly testable, so a change in ordering
 *   shows up as a diff before you even look at query outcomes;
 * - matchers stay simple and context-unaware.
 *
 * To add a context dimension (a feature gate, a user attribute), add the field
 * to `IntentContext` and branch on it here. Nothing else needs to change.
 */
export const resolveMatchers = (context: IntentContext = {}): readonly IntentMatcher[] => {
	if (context.primaryProduct === PRIMARY_PRODUCT.JIRA) {
		return JIRA_MATCHERS;
	}

	return UNIVERSAL_MATCHERS;
};
